import { useState, useEffect } from 'react';
import api from '../api';
import {
  Zap,
  Save,
  RefreshCw,
  RotateCcw,
  Cake,
  Calendar,
  Flame,
  Clock,
  Mail,
  BarChart3,
  TrendingUp,
  Trophy,
  Target,
} from 'lucide-react';
import toast from 'react-hot-toast';

const NOTIFICATION_TYPES = [
  { key: 'birthdayGreetingsEnabled', label: 'Birthday Greetings', icon: Cake, description: 'Send birthday wishes on user birthdays' },
  { key: 'anniversaryGreetingsEnabled', label: 'Membership Anniversary', icon: Calendar, description: 'Celebrate membership anniversaries' },
  { key: 'loginStreakEnabled', label: 'Login Streak Milestones', icon: Flame, description: 'Celebrate streak milestones (3, 7, 14, 30+ days)' },
  { key: 'streakAtRiskEnabled', label: 'Streak At-Risk Alerts', icon: Clock, description: 'Remind users before they lose their streak' },
  { key: 'welcomeBackEnabled', label: 'Welcome Back Messages', icon: Mail, description: 'Re-engage inactive users (7+ days)' },
  { key: 'weeklySummaryEnabled', label: 'Weekly Summary', icon: BarChart3, description: 'Sunday evening activity recaps' },
  { key: 'tierProgressEnabled', label: 'Tier Progress Updates', icon: TrendingUp, description: 'Notify at 25%, 50%, 75% to next tier' },
  { key: 'firstSessionCongratsEnabled', label: 'First Session Congrats', icon: Trophy, description: 'Celebrate first poker session' },
  { key: 'achievementNudgesEnabled', label: 'Achievement Nudges', icon: Target, description: 'Alert when close to unlocking achievements' },
  { key: 'sessionCompleteEnabled', label: 'Long Session Congrats', icon: Zap, description: 'Celebrate sessions over 2 hours' },
];

export default function EngagementSettings() {
  const [settings, setSettings] = useState(null);
  const [modified, setModified] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const res = await api.get('/admin/settings/engagement-notifications');
      setSettings(res.data.settings);
      setModified({});
    } catch (err) {
      toast.error('Failed to load engagement settings');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const getValue = (key) => {
    if (modified[key] !== undefined) return modified[key];
    return settings?.[key] ?? true;
  };

  const handleToggle = (key) => {
    const currentValue = getValue(key);
    setModified(prev => ({ ...prev, [key]: !currentValue }));
  };

  const handleSave = async () => {
    if (Object.keys(modified).length === 0) {
      toast.error('No changes to save');
      return;
    }
    try {
      setSaving(true);
      await api.put('/admin/settings/engagement-notifications', modified);
      toast.success('Settings saved');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    if (!confirm('Reset all engagement notification settings to defaults?')) return;
    try {
      setSaving(true);
      await api.post('/admin/settings/engagement-notifications/reset');
      toast.success('Settings reset to defaults');
      fetchSettings();
    } catch (err) {
      toast.error('Failed to reset settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  const hasChanges = Object.keys(modified).length > 0;
  const masterEnabled = getValue('engagementNotificationsEnabled');

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white">Automated Engagement Notifications</h2>
          <p className="text-sm text-gray-400">Control smart notifications that drive user engagement</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={fetchSettings} className="p-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
            <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
          </button>
          <button onClick={handleReset} className="flex items-center gap-2 px-3 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600">
            <RotateCcw size={16} /> Reset
          </button>
          <button
            onClick={handleSave}
            disabled={!hasChanges || saving}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg ${
              hasChanges ? 'bg-amber-500 text-black hover:bg-amber-400' : 'bg-gray-700 text-gray-500 cursor-not-allowed'
            }`}
          >
            <Save size={16} /> {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {/* Unsaved changes */}
      {hasChanges && (
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 flex items-center justify-between">
          <span className="text-amber-400">You have unsaved changes</span>
          <button onClick={() => setModified({})} className="text-gray-400 hover:text-white text-sm">Discard</button>
        </div>
      )}

      {/* Master Toggle */}
      <div className={`rounded-xl p-4 ${masterEnabled ? 'bg-green-500/10 border border-green-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Zap className={masterEnabled ? 'text-green-400' : 'text-red-400'} size={24} />
            <div>
              <p className="text-white font-semibold">Master Switch</p>
              <p className="text-sm text-gray-400">
                {masterEnabled ? 'All automated notifications are enabled' : 'All automated notifications are disabled'}
              </p>
            </div>
          </div>
          <button
            onClick={() => handleToggle('engagementNotificationsEnabled')}
            className={`relative inline-flex h-7 w-14 items-center rounded-full transition-colors ${
              masterEnabled ? 'bg-green-500' : 'bg-gray-600'
            }`}
          >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
              masterEnabled ? 'translate-x-8' : 'translate-x-1'
            }`} />
          </button>
        </div>
      </div>

      {/* Individual Toggles */}
      <div className={`bg-gray-800 rounded-xl overflow-hidden ${!masterEnabled ? 'opacity-50' : ''}`}>
        <div className="p-4 border-b border-gray-700">
          <h3 className="text-white font-medium">Notification Types</h3>
        </div>
        <div className="divide-y divide-gray-700">
          {NOTIFICATION_TYPES.map(({ key, label, icon: Icon, description }) => {
            const enabled = getValue(key);
            return (
              <div key={key} className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${enabled && masterEnabled ? 'bg-amber-500/20' : 'bg-gray-700'}`}>
                    <Icon size={18} className={enabled && masterEnabled ? 'text-amber-400' : 'text-gray-500'} />
                  </div>
                  <div>
                    <p className="text-white font-medium">{label}</p>
                    <p className="text-sm text-gray-500">{description}</p>
                  </div>
                </div>
                <button
                  onClick={() => handleToggle(key)}
                  disabled={!masterEnabled}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    enabled ? 'bg-amber-500' : 'bg-gray-600'
                  } ${!masterEnabled ? 'cursor-not-allowed' : ''}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    enabled ? 'translate-x-6' : 'translate-x-1'
                  }`} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {/* Schedule Info */}
      <div className="bg-gray-800 rounded-xl p-4">
        <h3 className="text-white font-medium mb-3">Notification Schedule (Pacific Time)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400">Birthday & Anniversary</p>
            <p className="text-white font-medium">9:00 AM / 9:30 AM</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400">Welcome Back</p>
            <p className="text-white font-medium">2:00 PM</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400">Streak At-Risk</p>
            <p className="text-white font-medium">8:00 PM</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400">Weekly Summary</p>
            <p className="text-white font-medium">Sunday 6:00 PM</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400">Tier Progress</p>
            <p className="text-white font-medium">11 AM & 5 PM</p>
          </div>
          <div className="bg-gray-700/50 rounded-lg p-3">
            <p className="text-gray-400">Session/Achievement</p>
            <p className="text-white font-medium">Real-time</p>
          </div>
        </div>
      </div>
    </div>
  );
}
